import React, { ReactNode, useEffect } from "react";
import "./App.scss";
import { BrowserRouter as Router, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import Header from "./components/header/Header";
import { Footer } from "./components/footer/footer";
import SearchResultPage from "./pages/search-page/searchPage";
import RoomBooking from "./pages/room-booking/RoomBooking";
import InfoBanner from "./components/info-banner/InfoBanner";
import ServiceRequestPage from "./pages/service-request/ServiceRequest";
import CustomRedirect from "./components/custom-redirect/CustomRedirect";
import TilePage from "./pages/tile-page/TilePage";
import AboutUs from "./pages/about-us/AboutUs";
import LoginPage from "./pages/login/LoginPage";
import { HttpServicePost, HttpServicePostLiveAgentChat } from "./services/httpService";
import { AdminPage } from "./pages/admin-page/admin.page";
import { useUniqueUsers } from "./components/unique-users/unique.users";
import { initializeQualtrics } from "./util/qualtrics";
import { CSSVariables, useTheme } from "@me8eon/themes";
import { PwaInstaller } from "@me8eon/pwa_installer";
import { usePwaFlag } from "./hooks/usePwaFlag";
import { useJwtToken, useUserData } from "@me8eon/react_authentication";
import { PublicClientApplication } from "@azure/msal-browser";
import { AnalyticsTracker } from "@me8eon/analytics_tracker";
import { Sidebar } from "@me8eon/sidebar_layout";
import { RecentChat } from "@me8eon/sessions_messages";
import { NewAiPage } from "./components/ai-assist-refactored/NewAiPage";
import { DevMode } from "@me8eon/devmode";
import { useTranslation } from "@me8eon/translation";
import useSearchStore from "./store/searchStore";
import { useLanguage } from "@me8eon/language";
import { IntentDevMode } from "@me8eon/react_intent";
import AiAssistantPage from "./modules/AiAssist/AiAssistant.page";
import { Communicate } from "./pages/communicate";
import { useFeatureFlag } from "@me8eon/featureflags";
import { metaHumanFFName } from "./pages/communicate/audio.feature.flag";
import { EnvironmentConfig } from "@me8eon/config";
import AiSharePage from "./modules/AiAssist/AiShare.page";
import OnboardingPortal from './pages/onboarding-portal/OnboardingPortal';
import { ROLE_ONBOARDING_PORTAL_USER, ROLE_ONBOARDING_PORTAL_REVIEWER } from './pages/onboarding-portal/OnboardingPortal.constants';
import { ROLE_PRODUCT_TEAM_DASHBOARD } from './pages/product-owner-dashboard/ProductOwnerDashboard.constants';
import ProductOwnerDashboard from './pages/product-owner-dashboard/ProductOwnerDashboard';

function ProtectedRoutes() {
    const userData = useUserData();
    const location = useLocation();

    const loggedIn = userData.loggedIn;
    return loggedIn ? <Outlet /> : <Navigate to="/" replace state={{ from: location }} />;
}

// interface EonUiSectionWrapperProps {
//     children: ReactNode;
//     removeTopSpacing?: boolean;
//     removeBottomSpacing?: boolean;
//     fullWidth?: boolean;
// }


type AppProps = {
    msalInstance: PublicClientApplication;
}

export function App({ msalInstance }: AppProps) {
    const theme = useTheme();
    const orgName = theme.value;

    const uniqueUsers = useUniqueUsers();
    const showPwa = usePwaFlag();
    const metaHumanFF = useFeatureFlag(metaHumanFFName);
    const userData = useUserData();
    const idToken = useJwtToken();
    const { loggedIn, localAccountId, actualRoles } = useUserData();
    const [, setLanguage] = useLanguage();
    const { i18n } = useTranslation();

    useEffect(() => {
        if (userData.loggedIn) {
            idToken()//.then(token => fetchAndStoreDLSApiKey(userData.email, token)) //done elsewhere now
                .then(() =>
                    uniqueUsers(userData.email, { ctry: userData.country, CompanyName: userData.company, role: userData.currentRole }));
            HttpServicePostLiveAgentChat();
            initializeQualtrics();
        }
    }, [userData]);

    const {
        fetchUserPref,
        setUserId,
    } = useSearchStore();

    useEffect(() => {
        const fetchData = async () => {
            if (loggedIn) {
                setUserId(localAccountId);
                await fetchUserPref(idToken, localAccountId);
                const lang: string | null = localStorage.getItem("i18nextLng");
                if (i18n.language !== lang && lang) {
                    i18n.changeLanguage(lang);
                    setLanguage(lang);

                }
            }
        };
        fetchData();
    }, [localAccountId, loggedIn]);

    const globalCSSvariables: CSSVariables = {
        ...theme.dataLayout.colorVariables,
    };

    // const EonUiSectionWrapper: React.FC<EonUiSectionWrapperProps> = ({ children, ...props }) => {
    //     return !loggedIn ? children : <EonUiSection {...props}>{children}</EonUiSection>;
    // };

    return (
        <>
            <Router>
                <AnalyticsTracker />
                <div className={`wrapper org-scheme org-scheme-${orgName}`} style={globalCSSvariables}>
                    <Header orgName={orgName} msalInstance={msalInstance} />
                    {showPwa && <PwaInstaller />}
                    <Sidebar
                        sidebar={
                            <>
                                <div className="recentChat_wrapper">
                                    <RecentChat />
                                </div>
                            </>
                        }
                    >
                        <div className="content">
                            <DevMode />
                            {/* <EonUiSectionWrapper removeTopSpacing={true}> */}
                            <Routes>
                                <Route path="/" element={<LoginPage />} />
                                <Route element={<ProtectedRoutes />}>
                                    <Route
                                        path="/tile"
                                        element={<TilePage />}
                                    />
                                    <Route
                                        path="/ai"
                                        element={<AiSharePage />}
                                    />
                                    {metaHumanFF && !EnvironmentConfig?.isHigherEnv && (
                                        <Route
                                            path="/voice-assist"
                                            element={<Communicate />}
                                        />
                                    )}
                                    <Route path="/intent"
                                        element={<IntentDevMode />} />
                                    <Route
                                        path="/tile/share"
                                        element={<AiSharePage />}
                                    />
                                    <Route
                                        path="/admin"
                                        element={<AdminPage />}
                                    />
                                    <Route
                                        path="/search-result"
                                        element={<SearchResultPage />}
                                    />
                                    <Route
                                        path="/roombooking"
                                        element={<RoomBooking />}
                                    />
                                    <Route
                                        path="/my-approvals"
                                        element={
                                            <InfoBanner
                                                multiLangHeading="underDevelopmentMsg"
                                                icon="alert"
                                            />
                                        }
                                    />
                                    <Route
                                        path="/orders"
                                        element={
                                            <ServiceRequestPage type="orders" />
                                        }
                                    />
                                    <Route
                                        path="/incidents"
                                        element={
                                            <ServiceRequestPage type="incidents" />
                                        }
                                    />
                                    <Route
                                        path="/it-resources"
                                        element={
                                            <ServiceRequestPage type="itResources" />
                                        }
                                    />
                                    <Route
                                        path="/about-myhelp"
                                        element={<AboutUs />}
                                    />
                                    {(actualRoles.includes(ROLE_ONBOARDING_PORTAL_USER)||actualRoles.includes(ROLE_ONBOARDING_PORTAL_REVIEWER)) && <Route
                                        path="/onboarding-portal"
                                        element={<OnboardingPortal />}
                                    />}
                                   { (actualRoles.includes(ROLE_PRODUCT_TEAM_DASHBOARD) )  &&  <Route
                                        path="/analytics"
                                        element={<ProductOwnerDashboard />}
                                    />} 
                                   
                                </Route>
                                <Route
                                    path="/api/gingco/code"
                                    element={
                                        <CustomRedirect page="roombooking" />
                                    }
                                />
                                <Route
                                    path="/authorize/sapias"
                                    element={<CustomRedirect page="tile" />}
                                />
                                <Route path="*" element={<Navigate to="/" />} />
                            </Routes>
                            {/* </EonUiSectionWrapper> */}
                        </div>
                    </Sidebar>
                </div>
                <div className={`org-scheme org-scheme-${orgName}`}>
                    <Footer />
                </div>
            </Router>
        </>
    );
}


